import React from 'react';
import { View, StyleSheet } from 'react-native';
import { InventoryItem } from '../../../redux/inventorySlice';
import { FoodTile } from '../FoodTile';
import { usePantryName } from '../usePantryName';
import { Paprika, PaprikaShadows } from '../../../styles/theme';

interface DragGhostProps {
  item: InventoryItem;
  x: number;
  y: number;
}

/**
 * Renders a floating ghost card while dragging.
 * Shows FoodTile (30px) + name, rotated -3deg, with strong warm shadow.
 * Positioned absolutely and positioned relative to pantry container.
 */
export function DragGhost({ item, x, y }: DragGhostProps) {
  const pantryName = usePantryName();
  return (
    <View
      style={[
        styles.ghost,
        {
          left: x,
          top: y,
        },
      ]}
    >
      <View style={styles.content}>
        <FoodTile
          category={item.category}
          name={item.name}
          size={30}
          radius={9}
        />
        <View style={styles.nameWrapper}>
          <Text style={styles.name} numberOfLines={1}>
            {pantryName(item)}
          </Text>
        </View>
      </View>
    </View>
  );
}

// Use Text from react-native for web text rendering
const Text = ({ style, children, numberOfLines }: any) => {
  return (
    <div
      style={{
        fontFamily: 'Inter-SemiBold, -apple-system, BlinkMacSystemFont, sans-serif',
        fontSize: 13,
        fontWeight: '600',
        color: Paprika.textPrimary,
        overflow: numberOfLines ? 'hidden' : undefined,
        textOverflow: numberOfLines ? 'ellipsis' : undefined,
        whiteSpace: numberOfLines ? 'nowrap' : undefined,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

const styles = StyleSheet.create({
  ghost: {
    position: 'absolute',
    pointerEvents: 'none',
    transform: [{ translateX: '-40%' }, { translateY: '-50%' }, { rotate: '-3deg' }],
    zIndex: 90,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 12,
    backgroundColor: Paprika.surfaceLowest,
    borderWidth: 1,
    borderColor: Paprika.border,
    ...PaprikaShadows.card,
    // Strong warm shadow override for ghost
    shadowColor: '#52443c',
    shadowOffset: { width: 0, height: 14 },
    shadowOpacity: 0.32,
    shadowRadius: 34,
  },
  nameWrapper: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontSize: 13,
    fontWeight: '600',
    color: Paprika.textPrimary,
  },
});
